"use client";

import { useEffect, useState } from "react";

interface IndustryHeroProps {
  svgPath: string;
}

export default function IndustryHero({ svgPath }: IndustryHeroProps) {
  const [svgContent, setSvgContent] = useState<string>("");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    fetch(svgPath)
      .then((res) => res.text())
      .then((text) => {
        setSvgContent(text);
        setIsLoaded(true);
      })
      .catch((err) => console.error("Failed to load SVG:", err));
  }, [svgPath]);

  useEffect(() => {
    if (!isLoaded) return;

    // Animate diagonal lines - find paths with stroke="#EDEDED"
    const diagonalLines = document.querySelectorAll<SVGPathElement>(
      'path[stroke="#EDEDED"]'
    );
    diagonalLines.forEach((line, index) => {
      const length = line.getTotalLength();
      line.style.strokeDasharray = String(length);
      line.style.strokeDashoffset = String(length);
      line.style.transition = "none";

      setTimeout(() => {
        line.style.transition = `stroke-dashoffset 1.3s cubic-bezier(0.42, 0, 0.58, 1)`;
        line.style.strokeDashoffset = "0";
      }, index * 200);
    });

    // Animate geometric shapes (polygons/diamonds) - find paths in filter groups
    const shapeGroups = document.querySelectorAll<SVGGElement>(
      'g[filter*="filter"] path'
    );
    shapeGroups.forEach((shape, index) => {
      const path = shape as SVGPathElement;
      path.style.opacity = "0";
      path.style.transform = "translateY(-60px)";
      path.style.transition = "none";

      setTimeout(() => {
        path.style.transition = "opacity 0.8s ease-out, transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)";
        path.style.opacity = "1";
        path.style.transform = "translateY(0)";
      }, 300 + index * 150);
    });

    // Animate text paths - find paths with fill="#DDDDDD" that are likely text
    const textPaths = document.querySelectorAll<SVGPathElement>(
      'path[fill="#DDDDDD"]'
    );
    // Filter out very short paths (likely not text)
    const filteredTextPaths = Array.from(textPaths).filter((path) => {
      const length = path.getTotalLength();
      return length > 100; // Only animate longer paths (text)
    });

    filteredTextPaths.forEach((path, index) => {
      const length = path.getTotalLength();
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
      path.style.stroke = "#DDDDDD";
      path.style.fill = "transparent";
      path.style.strokeWidth = "1";
      path.style.transition = "none";

      setTimeout(() => {
        const drawDuration = 0.6;
        path.style.transition = `stroke-dashoffset ${drawDuration}s cubic-bezier(0.65, 0, 0.35, 1)`;
        path.style.strokeDashoffset = "0";

        setTimeout(() => {
          path.style.transition = "fill 0.8s ease-in-out, stroke 0.3s ease-out";
          path.style.fill = "#DDDDDD";
          path.style.stroke = "transparent";
        }, drawDuration * 1000);
      }, 500 + index * 100);
    });

    // Animate bars/rectangles with specific colors
    const bars = document.querySelectorAll<SVGRectElement>(
      'rect[fill="#D53333"], rect[fill="#272D57"]'
    );
    bars.forEach((bar, index) => {
      bar.style.opacity = "0";
      bar.style.transform = "scaleY(0)";
      bar.style.transformOrigin = "bottom";
      bar.style.transition = "none";

      setTimeout(() => {
        bar.style.transition = "opacity 0.6s ease-out, transform 0.8s ease-out";
        bar.style.opacity = "1";
        bar.style.transform = "scaleY(1)";
      }, 800 + index * 150);
    });

    // Animate badge rectangles
    const badges = document.querySelectorAll<SVGRectElement>(
      'rect[fill="#D53333"][rx="10"]'
    );
    badges.forEach((badge, index) => {
      badge.style.opacity = "0";
      badge.style.transform = "scale(0)";
      badge.style.transition = "none";

      setTimeout(() => {
        badge.style.transition = "opacity 0.5s ease-out, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)";
        badge.style.opacity = "1";
        badge.style.transform = "scale(1)";
      }, 1000 + index * 100);
    });
  }, [isLoaded]);

  if (!isLoaded) {
    return (
      <section className="relative w-full">
        <div className="relative w-full min-h-[260px] sm:min-h-[340px] md:min-h-[480px] lg:min-h-[80vh] section-background-filler flex items-center justify-center" />
      </section>
    );
  }

  return (
    <section className="relative w-full">
      <div className="relative w-full min-h-[260px] sm:min-h-[340px] md:min-h-[480px] lg:min-h-[80vh] section-background-filler flex items-center justify-center overflow-hidden">
        <div
          className="industry-hero-svg-wrapper w-full h-full flex items-center justify-center"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      </div>
    </section>
  );
}

